import { useNavigate } from 'react-router-dom';
import React, { FC, useState, useEffect, useCallback, useRef } from 'react';
import { Box, Flex, Text, Center } from '@chakra-ui/react';
import PostEventReport from '../PostEventReport/PostEventReport';
import SeatCardPrint from '../SeatCardPrint/SeatCardPrint';
import { db } from '../../firebase';
import { useTDTier } from '../../hooks/useTDTier';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';

// ── TYPES ──────────────────────────────────────────────────────────────────
interface BlindLevel {
  index: number;
  isBreak: boolean;
  label: string;
  smallBlind?: number;
  bigBlind?: number;
  ante?: number;
  duration: number; // minutes
}

interface Player {
  id: string;
  name: string;
  buyIn: number;
  rebuys: number;
  addOns: number;
  eliminatedAt?: number; // level index
  finishPosition?: number;
  registeredAt: number;
}

interface PCTDSClockState {
  players: Player[];
  isRunning: boolean;
  currentLevelIndex: number;
  secondsLeft: number;
  elapsed: number;
  prizePool: number;
  rebuyCount: number;
  addOnCount: number;
  nextFinishPosition: number;
  isHandForHand: boolean;
}

interface PCTDSClockProps {
  eventId?: string;
  event: {
    name?: string;
    startingChips?: number;
    blindLevelDuration?: number;
    buyIn?: number;
    maxPlayers?: number;
    payouts?: { position: string; amount: string }[];
    venueId?: string;
    structureId?: string;
    charityId?: string;
    charityName?: string;
    charityPct?: number;
    licenseType?: string;
    admin_uid?: string;
    privacy?: string;
    city?: string;
    province?: string;
  };
}

// ── CONSTANTS ──────────────────────────────────────────────────────────────
const F = { ui:"'Rajdhani','Arial',sans-serif", mono:"'Share Tech Mono',monospace" };

const buildLevels = (duration: number): BlindLevel[] => {
  const structure = [
    [25,50,0],[50,100,0],[75,150,0],[100,200,25],[150,300,25],[200,400,50],
    [300,600,75],[400,800,100],[500,1000,100],[600,1200,200],[800,1600,200],
    [1000,2000,300],[1500,3000,300],[2000,4000,500],[3000,6000,500],
    [4000,8000,1000],[5000,10000,1000],[6000,12000,2000],[8000,16000,2000],
    [10000,20000,3000],[15000,30000,3000],[20000,40000,5000],
  ];
  const levels: BlindLevel[] = [];
  structure.forEach(([sb, bb, ante], i) => {
    levels.push({ index:i*2, isBreak:false, label:`Level ${i+1}`,
      smallBlind:sb, bigBlind:bb, ante, duration });
    if ((i+1) % 3 === 0 && i < structure.length-1) {
      levels.push({ index:i*2+1, isBreak:true, label:'BREAK', duration:15 });
    }
  });
  return levels;
};

const fmt = (s: number) =>
  `${String(Math.floor(s/60)).padStart(2,'0')}:${String(s%60).padStart(2,'0')}`;

const fmtElapsed = (s: number) => {
  const h = Math.floor(s/3600);
  const m = Math.floor((s%3600)/60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

// ── LOCAL STORAGE PERSISTENCE (offline-first) ──────────────────────────────
const STORAGE_KEY = (id: string) => `pctds_clock_${id}`;

const loadLocal = (eventId: string): Partial<PCTDSClockState> | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY(eventId));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const saveLocal = (eventId: string, state: PCTDSClockState) => {
  try { localStorage.setItem(STORAGE_KEY(eventId), JSON.stringify(state)); } catch {}
};

// ── MAIN COMPONENT ─────────────────────────────────────────────────────────
const PCTDSClock: FC<PCTDSClockProps> = ({
 eventId, event }) => {
  const duration = event.blindLevelDuration || 20;
  const builtLevels = buildLevels(duration);
  const levels = customLevels ?? builtLevels;
  const buyIn = event.buyIn || 0;
  const charityPct = (event as any).charityPct || 0;
  const addonLevel = (event as any).addOn?.isAddOn ? ((event as any).late_reg_level || 6) : null;
  const addonInstructions = (event as any).add_on_instructions ||
    (event as any).addOn?.isAddOn ? 'Add-on period is now open. See TD for details.' : null;
  const charityName = (event as any).charityName || '';

  const [isRunning, setIsRunning] = useState(false);
  const [levelIdx, setLevelIdx] = useState(0);
  const [secsLeft, setSecsLeft] = useState(levels[0].duration * 60);
  const [elapsed, setElapsed] = useState(0);
  const [players, setPlayers] = useState<Player[]>([]);
  const [prizePool, setPrizePool] = useState(0);
  const [nextPos, setNextPos] = useState(1);
  const [isHandForHand, setIsHandForHand] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [soundOn, setSoundOn] = useState(true);
  const [warningFired, setWarningFired] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [venueProfile, setVenueProfile] = useState<any>(null);
  const [charityPool, setCharityPool] = useState(0);
  const [showReport, setShowReport] = useState(false);
  const navigate = useNavigate();
  const { canUseSeatCards } = useTDTier();
  const [showSeatCards, setShowSeatCards] = useState(false);
  const [showAddonNotice, setShowAddonNotice] = useState(false);
  const [addonNoticeDismissed, setAddonNoticeDismissed] = useState(false);
  const [customLevels, setCustomLevels] = useState<BlindLevel[]|null>(null);

  // Load blind structure from saved structure doc
  useEffect(() => {
    const loadStructure = async () => {
      const sid = (event as any).structureId;
      const adminUid = (event as any).admin_uid;
      if (!sid || !adminUid) return;
      try {
        const { doc: d2, getDoc: gd } = await import('firebase/firestore');
        const snap = await gd(d2(db, 'users', adminUid, 'structures', sid));
        if (!snap.exists()) return;
        const s = snap.data();
        const rawLevels: string[] = s.levels_array ?? [];
        const dur: number = s.blind_level_duration ?? duration;
        const parsed: BlindLevel[] = rawLevels.map((line: string, i: number) => {
          const t = line.trim();
          if (t.toUpperCase().startsWith('BREAK')) {
            const mins = parseInt(t.replace(/[^0-9]/g,'')) || 15;
            return { index:i, isBreak:true, label:'BREAK', duration:mins };
          }
          const parts = t.split('/').map((p: string) => parseInt(p.replace(/[^0-9]/g,''))||0);
          return { index:i, isBreak:false, label:`Level ${i+1}`,
            smallBlind:parts[0]||0, bigBlind:parts[1]||0, ante:parts[2]||0, duration:dur };
        });
        if (parsed.length > 0) setCustomLevels(parsed);
      } catch {}
    };
    loadStructure();
  }, [(event as any).structureId, (event as any).admin_uid]);

  // Load venue branding
  useEffect(() => {
    const loadVenue = async () => {
      const vid = (event as any).venueId;
      if (!vid) return;
      try {
        const { doc: d2, getDoc: gd } = await import('firebase/firestore');
        const snap = await gd(d2(db, 'venues', vid));
        if (snap.exists()) setVenueProfile({ id: snap.id, ...snap.data() });
      } catch {}
    };
    loadVenue();
  }, [(event as any).venueId]);

  // Panels
  const [showPlayerPanel, setShowPlayerPanel] = useState(false);
  const [showEliminatePanel, setShowEliminatePanel] = useState(false);
  const [showRebuyPanel, setShowRebuyPanel] = useState(false);
  const [showPayoutPanel, setShowPayoutPanel] = useState(false);

  // Player input
  const [newPlayerName, setNewPlayerName] = useState('');
  const [selectedElimUid, setSelectedElimUid] = useState('');

  const audioCtx = useRef<AudioContext | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const stateRef = useRef<PCTDSClockState | null>(null);

  const currentLevel = levels[levelIdx];
  const activePlayers = players.filter(p => !p.finishPosition);
  const eliminatedPlayers = players.filter(p => p.finishPosition).sort((a,b) => (b.finishPosition??0)-(a.finishPosition??0));
  const totalEntries = players.length;
  const avgStack = activePlayers.length > 0
    ? Math.floor((event.startingChips ?? 10000) * totalEntries / activePlayers.length)
    : 0;
  const payouts = event.payouts ?? [];
  const itm = payouts.length;
  const onBubble = activePlayers.length === itm + 1;
  const inMoney = activePlayers.length <= itm;

  // Online/offline detection
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', down); };
  }, []);

  // Load from localStorage on mount
  useEffect(() => {
    if (!eventId) return;
    const saved = loadLocal(eventId);
    if (saved) {
      if (saved.players) setPlayers(saved.players);
      if (saved.currentLevelIndex !== undefined) setLevelIdx(saved.currentLevelIndex);
      if (saved.secondsLeft !== undefined) setSecsLeft(saved.secondsLeft);
      if (saved.elapsed !== undefined) setElapsed(saved.elapsed);
      if (saved.prizePool !== undefined) setPrizePool(saved.prizePool);
      if (saved.nextFinishPosition !== undefined) setNextPos(saved.nextFinishPosition);
      if (saved.isHandForHand !== undefined) setIsHandForHand(saved.isHandForHand);
    }
  }, [eventId]);

  // Save to localStorage on every state change
  const persistState = useCallback(() => {
    if (!eventId) return;
    const state: PCTDSClockState = {
      players, isRunning, currentLevelIndex: levelIdx, secondsLeft: secsLeft,
      elapsed, prizePool, rebuyCount: players.reduce((a,p) => a+p.rebuys, 0),
      addOnCount: players.reduce((a,p) => a+p.addOns, 0),
      nextFinishPosition: nextPos, isHandForHand,
    };
    stateRef.current = state;
    saveLocal(eventId, state);
  }, [players, isRunning, levelIdx, secsLeft, elapsed, prizePool, nextPos, isHandForHand, eventId]);

  useEffect(() => { persistState(); }, [persistState]);

  // Sync to Firestore when online
  const syncToFirestore = useCallback(async () => {
    if (!eventId || !online || !stateRef.current) return;
    try {
      const s = stateRef.current;
      const clockState = {
        state: isRunning ? 'running' : 'paused',
        currentLevelIndex: s.currentLevelIndex,
        secondsLeft: s.secondsLeft,
        isRunning: s.isRunning,
        eventName: event.name ?? '',
        prizePool: s.prizePool,
        activePlayers: activePlayers.length,
        totalEntries: s.players.length,
        isHandForHand: s.isHandForHand,
        updatedAt: Date.now(),
      };

      // Write to event-scoped clock state (for Dashboard break sync)
      await setDoc(doc(db, 'events', eventId, 'clock', 'state'), clockState, { merge: true });

      // Write to global clock_feed (for ICE / landing page live games)
      await setDoc(doc(db, 'clock_feed', eventId), {
        ...clockState,
        privacy: event.privacy ?? 'public',
        city: event.city ?? null,
        province: event.province ?? null,
        charityName: event.charityName ?? null,
        charityPct: event.charityPct ?? null,
        venueId: event.venueId ?? null,
      }, { merge: true });
    } catch {}
  }, [eventId, online, isRunning, activePlayers.length, event.name]);

  // Clock tick
  useEffect(() => {
    if (!isRunning) return;
    const t = setInterval(() => {
      setSecsLeft(s => {
        if (s <= 1) {
          // Level complete
          const nextIdx = Math.min(levelIdx + 1, levels.length - 1);
          setLevelIdx(nextIdx);
          setWarningFired(false);
          // Check if this is the add-on level
          if (addonLevel && nextIdx === addonLevel && !addonNoticeDismissed) {
            setShowAddonNotice(true);
          }
          const next = levels[nextIdx];
          return next.duration * 60;
        }
        // Warning at 60s
        if (s === 61 && soundOn && !warningFired) {
          playWarning();
          setWarningFired(true);
        }
        return s - 1;
      });
      setElapsed(e => e + 1);
    }, 1000);
    return () => clearInterval(t);
  }, [isRunning, levelIdx, levels, soundOn, warningFired]);

  // Sync to Firestore every 10s when running
  useEffect(() => {
    if (!isRunning) return;
    const t = setInterval(syncToFirestore, 10000);
    return () => clearInterval(t);
  }, [isRunning, syncToFirestore]);

  // Audio
  const playWarning = () => {
    try {
      if (!audioCtx.current) audioCtx.current = new AudioContext();
      const ctx = audioCtx.current;
      [0, 0.3, 0.6].forEach(delay => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.frequency.value = 880;
        gain.gain.setValueAtTime(0.3, ctx.currentTime + delay);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.4);
        osc.start(ctx.currentTime + delay);
        osc.stop(ctx.currentTime + delay + 0.4);
      });
    } catch {}
  };

  const playLevelUp = () => {
    try {
      if (!audioCtx.current) audioCtx.current = new AudioContext();
      const ctx = audioCtx.current;
      [440, 550, 660].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.4, ctx.currentTime + i * 0.15);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.15 + 0.3);
        osc.start(ctx.currentTime + i * 0.15);
        osc.stop(ctx.currentTime + i * 0.15 + 0.3);
      });
    } catch {}
  };

  // Actions
  const toggleRun = () => {
    if (!isRunning) audioCtx.current = new AudioContext();
    setIsRunning(r => !r);
    syncToFirestore();
  };

  const nextLevel = () => {
    if (soundOn) playLevelUp();
    setLevelIdx(i => Math.min(i + 1, levels.length - 1));
    setSecsLeft(levels[Math.min(levelIdx + 1, levels.length - 1)].duration * 60);
    setWarningFired(false);
  };

  const prevLevel = () => {
    setLevelIdx(i => Math.max(i - 1, 0));
    setSecsLeft(levels[Math.max(levelIdx - 1, 0)].duration * 60);
    setWarningFired(false);
  };

  const addPlayer = () => {
    if (!newPlayerName.trim()) return;
    const player: Player = {
      id: Date.now().toString(),
      name: newPlayerName.trim(),
      buyIn, rebuys: 0, addOns: 0,
      registeredAt: levelIdx,
    };
    setPlayers(p => [...p, player]);
    const charityAmt = Math.floor(buyIn * charityPct / 100);
    setCharityPool(cp => cp + charityAmt);
    setPrizePool(pp => pp + buyIn - charityAmt);
    setNewPlayerName('');
    setNextPos(n => Math.max(n, players.length + 2));
  };

  const addRebuy = (playerId: string) => {
    setPlayers(ps => ps.map(p => p.id === playerId ? { ...p, rebuys: p.rebuys + 1 } : p));
    const charityAmt = Math.floor(buyIn * charityPct / 100);
    setCharityPool(cp => cp + charityAmt);
    setPrizePool(pp => pp + buyIn - charityAmt);
  };

  const eliminatePlayer = (playerId: string) => {
    const pos = activePlayers.length;
    setPlayers(ps => ps.map(p => p.id === playerId
      ? { ...p, finishPosition: pos, eliminatedAt: levelIdx }
      : p));
    if (onBubble && soundOn) playWarning();
    setSelectedElimUid('');
    setShowEliminatePanel(false);
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      containerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
    setIsFullscreen(f => !f);
  };

  // Payout display
  const getPayoutForPosition = (pos: number) => {
    const entry = payouts[pos - 1];
    if (!entry) return null;
    if (entry.amount.includes('%')) {
      const pct = parseFloat(entry.amount) / 100;
      return `$${Math.floor(prizePool * pct).toLocaleString()}`;
    }
    return entry.amount;
  };

  const nextPayout = inMoney && activePlayers.length > 0
    ? getPayoutForPosition(activePlayers.length) : null;
  const nextElim = !inMoney && activePlayers.length > itm + 1
    ? getPayoutForPosition(itm) : null;

  // Colors — use venue branding if available
  const primary = venueProfile?.brand_color_primary ?? '#19E6CF';
  const gold = venueProfile?.brand_color_secondary ?? '#D4A017';
  const red = '#EF4444';
  const green = '#22C55E';
  const levelColor = currentLevel.isBreak ? gold : primary;

  // ── FULLSCREEN CLOCK FACE ─────────────────────────────────────────────
  const ClockFace = () => (
    <Box ref={containerRef} bg="#080A0E" minH="100vh" position="relative"
      style={{ fontFamily: F.ui }}>

      {/* Status bar */}
      <Flex justify="space-between" align="center" px={4} py={2}
        bg="#0A0A0A" borderBottom="1px solid #1E2330">
        <Flex align="center" gap={3}>
          <Box w="8px" h="8px" borderRadius="full"
            bg={online ? green : red}
            style={{ boxShadow: `0 0 6px ${online ? green : red}` }} />
          <Text style={{ fontFamily: F.mono, fontSize: 10, color: '#6B7590',
            letterSpacing: '0.15em' }}>
            {online ? 'LIVE' : 'OFFLINE'} · {fmtElapsed(elapsed)}
          </Text>
        </Flex>
        <Text style={{ fontFamily: F.ui, fontSize: 14, fontWeight: 700,
          color: '#F0F4FF' }}>
          {event.name ?? 'Tournament'}
        </Text>
        <Flex gap={2}>
          <Box as="button" px={2} py={1} bg="transparent" border="none"
            fontSize="16px" cursor="pointer" onClick={() => setSoundOn(s => !s)}>
            {soundOn ? '🔊' : '🔇'}
          </Box>
          <Box as="button" px={2} py={1} bg="transparent" border="none"
            fontSize="16px" cursor="pointer" onClick={toggleFullscreen}>
            {isFullscreen ? '⊡' : '⛶'}
          </Box>
        </Flex>
      </Flex>

      {/* Hand-for-hand banner */}
      {isHandForHand && (
        <Box bg="#1A0F00" borderBottom={`2px solid ${gold}`} py={2} textAlign="center">
          <Text style={{ fontFamily: F.mono, fontSize: 12, color: gold,
            letterSpacing: '0.2em' }}>⚠️ HAND FOR HAND · BUBBLE PLAY</Text>
        </Box>
      )}

      {/* On bubble alert */}
      {onBubble && !isHandForHand && (
        <Box bg="#2D0D0D" borderBottom={`2px solid ${red}`} py={2} textAlign="center">
          <Text style={{ fontFamily: F.mono, fontSize: 12, color: red,
            letterSpacing: '0.2em' }}>🫧 BUBBLE — {activePlayers.length} PLAYERS REMAINING</Text>
        </Box>
      )}

      {/* In the money */}
      {inMoney && activePlayers.length > 1 && (
        <Box bg="#0A1A10" borderBottom={`2px solid ${green}`} py={2} textAlign="center">
          <Text style={{ fontFamily: F.mono, fontSize: 12, color: green,
            letterSpacing: '0.2em' }}>💰 IN THE MONEY · {activePlayers.length} PLAYERS</Text>
        </Box>
      )}

      <Flex direction="column" align="center" justify="center"
        minH="calc(100vh - 120px)" px={4} gap={4}>

        {/* Level */}
        <Box textAlign="center">
          <Text style={{ fontFamily: F.mono, fontSize: 13, color: levelColor,
            letterSpacing: '0.3em', marginBottom: 4 }}>
            {currentLevel.label.toUpperCase()}
          </Text>
          {!currentLevel.isBreak && (
            <Text style={{ fontFamily: F.mono, fontSize: 18, color: '#C8D0E0',
              letterSpacing: '0.1em' }}>
              {currentLevel.smallBlind?.toLocaleString()} / {currentLevel.bigBlind?.toLocaleString()}
              {currentLevel.ante ? ` · ANTE ${currentLevel.ante?.toLocaleString()}` : ''}
            </Text>
          )}
        </Box>

        {/* Timer */}
        <Text style={{ fontFamily: F.mono,
          fontSize: 'clamp(80px, 20vw, 160px)', fontWeight: 700,
          color: secsLeft <= 60 ? red : levelColor,
          lineHeight: 1, letterSpacing: '-0.02em',
          textShadow: `0 0 40px ${secsLeft <= 60 ? red : levelColor}44` }}>
          {fmt(secsLeft)}
        </Text>

        {/* Controls */}
        <Flex gap={3} align="center">
          <Box as="button" px={4} py={2} bg="#131720" border="1px solid #1E2330"
            borderRadius={6} fontSize="13px" color="#6B7590"
            fontFamily={F.ui} cursor="pointer" onClick={prevLevel}>
            ◀ Prev
          </Box>
          <Box as="button" px={8} py={4}
            bg={isRunning ? '#2D0D0D' : '#052E16'}
            border={`2px solid ${isRunning ? red : green}`}
            borderRadius={10} fontSize="18px" fontWeight="700"
            color={isRunning ? red : green}
            fontFamily={F.ui} cursor="pointer" onClick={toggleRun}
            style={{ minWidth: 140 }}>
            {isRunning ? '⏸ PAUSE' : '▶ START'}
          </Box>
          <Box as="button" px={4} py={2} bg="#131720" border="1px solid #1E2330"
            borderRadius={6} fontSize="13px" color="#6B7590"
            fontFamily={F.ui} cursor="pointer" onClick={nextLevel}>
            Next ▶
          </Box>
        </Flex>

        {/* Stats row */}
        <Flex gap={4} flexWrap="wrap" justify="center" mt={2}>
          {[
            { label: 'PLAYERS', value: `${activePlayers.length} / ${totalEntries}`, color: primary },
            { label: 'PRIZE POOL', value: `$${prizePool.toLocaleString()}`, color: gold },
            { label: 'AVG STACK', value: avgStack > 0 ? avgStack.toLocaleString() : '—', color: '#C8D0E0' },
            { label: 'LEVEL', value: `${levelIdx + 1} / ${levels.length}`, color: '#6B7590' },
            ...(charityPct > 0 ? [{ label: `${charityName || 'CHARITY'}`, value: `$${charityPool.toLocaleString()}`, color: '#EF4444' }] : []),
          ].map(s => (
            <Box key={s.label} textAlign="center" px={3}>
              <Text style={{ fontFamily: F.mono, fontSize: 9, color: '#6B7590',
                letterSpacing: '0.2em', marginBottom: 2 }}>{s.label}</Text>
              <Text style={{ fontFamily: F.ui, fontSize: 20, fontWeight: 700,
                color: s.color }}>{s.value}</Text>
            </Box>
          ))}
          {nextPayout && (
            <Box textAlign="center" px={3}>
              <Text style={{ fontFamily: F.mono, fontSize: 9, color: green,
                letterSpacing: '0.2em', marginBottom: 2 }}>NEXT OUT GETS</Text>
              <Text style={{ fontFamily: F.ui, fontSize: 20, fontWeight: 700,
                color: green }}>{nextPayout}</Text>
            </Box>
          )}
          {nextElim && (
            <Box textAlign="center" px={3}>
              <Text style={{ fontFamily: F.mono, fontSize: 9, color: '#6B7590',
                letterSpacing: '0.2em', marginBottom: 2 }}>BUBBLE PRIZE</Text>
              <Text style={{ fontFamily: F.ui, fontSize: 20, fontWeight: 700,
                color: gold }}>{nextElim}</Text>
            </Box>
          )}
        </Flex>

        {/* Action buttons */}
        <Flex gap={2} flexWrap="wrap" justify="center" mt={2}>
          {[
            { label: '+ Player', color: primary, action: () => setShowPlayerPanel(p => !p) },
            { label: '✗ Eliminate', color: red, action: () => setShowEliminatePanel(p => !p) },
            { label: '↩ Rebuy', color: gold, action: () => setShowRebuyPanel(p => !p) },
            { label: '💰 Payouts', color: green, action: () => setShowPayoutPanel(p => !p) },
            { label: isHandForHand ? '✓ H4H On' : 'H4H', color: isHandForHand ? gold : '#6B7590',
              action: () => setIsHandForHand(h => !h) },
            { label: '📋 Report', color: '#A855F7',
              action: () => setShowReport(true) },
            { label: '🖨 Cards', color: '#F97316',
              action: () => canUseSeatCards ? setShowSeatCards(true) : navigate('/pricing') },
          ].map(btn => (
            <Box key={btn.label} as="button" px={4} py={2}
              bg="#131720" border={`1px solid ${btn.color}44`}
              borderRadius={6} fontSize="13px" fontWeight="700"
              color={btn.color} fontFamily={F.ui} cursor="pointer"
              onClick={btn.action}>
              {btn.label}
            </Box>
          ))}
        </Flex>

        {/* Player registration panel */}
        {showPlayerPanel && (
          <Box bg="#131720" border={`1px solid ${primary}`} borderRadius={10}
            p={4} w="100%" maxW="400px">
            <Text style={{ fontFamily: F.mono, fontSize:11, color:primary,
              letterSpacing:'0.2em', marginBottom:12 }}>REGISTER PLAYER</Text>
            <Flex gap={2}>
              <input value={newPlayerName}
                onChange={e => setNewPlayerName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addPlayer()}
                placeholder="Player name..."
                style={{ flex:1, padding:'10px 12px', background:'#0D1118',
                  border:`1px solid #1E2330`, borderRadius:6, color:'#F0F4FF',
                  fontSize:14, fontFamily:F.ui }} />
              <Box as="button" px={4} py={2} bg="#052E16" border="1px solid #166534"
                borderRadius={6} fontSize="13px" fontWeight="700" color="#86EFAC"
                fontFamily={F.ui} cursor="pointer" onClick={addPlayer}>
                Add
              </Box>
            </Flex>
            <Text style={{ fontFamily:F.ui, fontSize:12, color:'#6B7590', marginTop:8 }}>
              Buy-in: ${buyIn} · Total registered: {players.length}
            </Text>
          </Box>
        )}

        {/* Eliminate panel */}
        {showEliminatePanel && activePlayers.length > 0 && (
          <Box bg="#131720" border={`1px solid ${red}`} borderRadius={10}
            p={4} w="100%" maxW="400px">
            <Text style={{ fontFamily:F.mono, fontSize:11, color:red,
              letterSpacing:'0.2em', marginBottom:12 }}>
              ELIMINATE PLAYER · {activePlayers.length} remaining → finishes {activePlayers.length}th
            </Text>
            <Flex direction="column" gap={2} maxH="200px" overflowY="auto">
              {activePlayers.map(p => (
                <Flex key={p.id} justify="space-between" align="center"
                  p={2} bg="#0D1118" borderRadius={6}>
                  <Text style={{ fontFamily:F.ui, fontSize:14, color:'#F0F4FF' }}>
                    {p.name}
                    {p.rebuys > 0 && <span style={{ color:gold, fontSize:11 }}> +{p.rebuys}R</span>}
                  </Text>
                  <Box as="button" px={3} py={1} bg="#2D0D0D"
                    border={`1px solid ${red}`} borderRadius={4}
                    fontSize="12px" fontWeight="700" color={red}
                    fontFamily={F.ui} cursor="pointer"
                    onClick={() => eliminatePlayer(p.id)}>
                    Bust Out
                  </Box>
                </Flex>
              ))}
            </Flex>
          </Box>
        )}

        {/* Rebuy panel */}
        {showRebuyPanel && activePlayers.length > 0 && (
          <Box bg="#131720" border={`1px solid ${gold}`} borderRadius={10}
            p={4} w="100%" maxW="400px">
            <Text style={{ fontFamily:F.mono, fontSize:11, color:gold,
              letterSpacing:'0.2em', marginBottom:12 }}>REBUY (adds ${buyIn} to prize pool)</Text>
            <Flex direction="column" gap={2} maxH="200px" overflowY="auto">
              {activePlayers.map(p => (
                <Flex key={p.id} justify="space-between" align="center"
                  p={2} bg="#0D1118" borderRadius={6}>
                  <Text style={{ fontFamily:F.ui, fontSize:14, color:'#F0F4FF' }}>
                    {p.name}
                    {p.rebuys > 0 && <span style={{ color:gold, fontSize:11 }}> ({p.rebuys} rebuys)</span>}
                  </Text>
                  <Box as="button" px={3} py={1} bg="#1A0F00"
                    border={`1px solid ${gold}`} borderRadius={4}
                    fontSize="12px" fontWeight="700" color={gold}
                    fontFamily={F.ui} cursor="pointer"
                    onClick={() => addRebuy(p.id)}>
                    Rebuy +$
                  </Box>
                </Flex>
              ))}
            </Flex>
          </Box>
        )}

        {/* Payout panel */}
        {showPayoutPanel && (
          <Box bg="#131720" border={`1px solid ${green}`} borderRadius={10}
            p={4} w="100%" maxW="400px">
            <Text style={{ fontFamily:F.mono, fontSize:11, color:green,
              letterSpacing:'0.2em', marginBottom:12 }}>
              PAYOUTS · Pool: ${prizePool.toLocaleString()}
            </Text>
            {payouts.length === 0 ? (
              <Text style={{ fontFamily:F.ui, fontSize:13, color:'#6B7590' }}>
                No payout structure set on this event.
              </Text>
            ) : (
              <Flex direction="column" gap={1} maxH="200px" overflowY="auto">
                {payouts.map((p, i) => (
                  <Flex key={i} justify="space-between" p={2}
                    bg={activePlayers.length === i + 1 ? '#0A1A10' : '#0D1118'}
                    borderRadius={4}
                    border={activePlayers.length === i + 1 ? `1px solid ${green}` : 'none'}>
                    <Text style={{ fontFamily:F.ui, fontSize:13, color:'#C8D0E0' }}>
                      {p.position}
                    </Text>
                    <Text style={{ fontFamily:F.mono, fontSize:13, color:green }}>
                      {getPayoutForPosition(i + 1) ?? p.amount}
                    </Text>
                  </Flex>
                ))}
              </Flex>
            )}
          </Box>
        )}

        {/* Elimination log */}
        {eliminatedPlayers.length > 0 && (
          <Box w="100%" maxW="400px">
            <Text style={{ fontFamily:F.mono, fontSize:10, color:'#6B7590',
              letterSpacing:'0.15em', marginBottom:6 }}>RESULTS</Text>
            <Flex direction="column" gap={1}>
              {eliminatedPlayers.slice(0, 5).map(p => (
                <Flex key={p.id} justify="space-between" px={3} py={1}
                  bg="#0D1118" borderRadius={4}>
                  <Text style={{ fontFamily:F.ui, fontSize:13, color:'#C8D0E0' }}>
                    {p.finishPosition}. {p.name}
                  </Text>
                  <Text style={{ fontFamily:F.mono, fontSize:11, color:gold }}>
                    {getPayoutForPosition(p.finishPosition!) ?? '—'}
                  </Text>
                </Flex>
              ))}
              {eliminatedPlayers.length > 5 && (
                <Text style={{ fontFamily:F.mono, fontSize:10, color:'#6B7590',
                  textAlign:'center' }}>
                  +{eliminatedPlayers.length - 5} more
                </Text>
              )}
            </Flex>
          </Box>
        )}
      </Flex>
    </Box>
  );

  return (
    <>
      <ClockFace />
      {/* Add-on Period Notice */}
      {showAddonNotice && (
        <Box position="fixed" inset={0} bg="rgba(0,0,0,0.85)" zIndex={900}
          display="flex" alignItems="center" justifyContent="center" px={4}>
          <Box bg="#131720" border="2px solid #19E6CF" borderRadius={16}
            p={8} maxW="480px" w="100%" textAlign="center">
            <Text style={{ fontSize:48, marginBottom:16 }}>➕</Text>
            <Text style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:13,
              color:'#19E6CF', letterSpacing:'0.25em', marginBottom:12 }}>
              ADD-ON PERIOD NOW OPEN
            </Text>
            <Text style={{ fontFamily:"'Rajdhani','Arial',sans-serif", fontSize:22,
              fontWeight:700, color:'#F0F4FF', lineHeight:1.5, marginBottom:8 }}>
              {addonInstructions}
            </Text>
            {(event as any).addOn?.addOnPrice > 0 && (
              <Text style={{ fontFamily:"'Rajdhani','Arial',sans-serif", fontSize:18,
                color:'#D4A017', marginBottom:24 }}>
                ${(event as any).addOn.addOnPrice} · {(event as any).addOn.addOnAmount?.toLocaleString()} chips
              </Text>
            )}
            <Box as="button" px={8} py={3} bg="#052E16"
              border="1px solid #22C55E" borderRadius={8}
              fontSize="15px" fontWeight="700" color="#86EFAC"
              fontFamily="'Rajdhani','Arial',sans-serif" cursor="pointer"
              onClick={() => { setShowAddonNotice(false); setAddonNoticeDismissed(true); }}>
              Got it — Continue Clock →
            </Box>
          </Box>
        </Box>
      )}
      {showSeatCards && (
        <SeatCardPrint
          eventId={eventId ?? ''}
          eventName={event.name ?? 'Tournament'}
          venueName={venueProfile?.name}
          buyIn={buyIn}
          rebuyAllowed={(event as any).rebuys?.isRebuys}
          addOnAllowed={(event as any).addOn?.isAddOn}
          lateRegLevel={(event as any).late_reg_level}
          players={players}
          onClose={() => setShowSeatCards(false)}
        />
      )}
      {showReport && (
        <PostEventReport
          eventId={eventId ?? ''}
          eventName={event.name ?? 'Tournament'}
          venueData={venueProfile}
          players={players}
          prizePool={prizePool}
          charityPool={charityPool}
          charityName={(event as any).charityName}
          payouts={event.payouts ?? []}
          buyIn={buyIn}
          levelIdx={levelIdx}
          elapsed={elapsed}
          seatingMode={(event as any).seatingMode}
          seatingLog={(event as any).seatingLog}
          onClose={() => setShowReport(false)}
        />
      )}
    </>
  );
};

export default PCTDSClock;
